import React, { useEffect, useRef, useState } from 'react';
import { AopLink, Card, InternalCard, Pill } from '../ui';
import { useCop } from '../CopApp';
import UpsellyMascot from '../UpsellyMascot';
import serviceImg from '../../../images/service.jpg';

const SECTIONS = [
	{ id: 'overview', label: 'What this platform is' },
	{ id: 'lifecycle', label: 'The action lifecycle' },
	{ id: 'modules', label: 'The eight modules' },
	{ id: 'concepts', label: 'Core concepts' },
	{ id: 'measurement', label: 'How we measure' },
	{ id: 'experiments', label: 'Experiment types' },
	{ id: 'governance', label: 'Governance & approvals' },
	{ id: 'autopilot', label: 'What Auto-Pilot means' },
	{ id: 'data', label: 'Data sources & freshness' },
	{ id: 'glossary', label: 'Glossary' },
];

const LIFECYCLE = ['Detect', 'Score', 'Recommend', 'Validate', 'Approve', 'Launch', 'Measure', 'Learn'];

const MODULES = [
	{
		ico: '◈',
		name: 'Command Center',
		view: 'command-center',
		text: 'The portfolio home. Shows what the AI found (segments, opportunities), what is waiting on you (pending actions), and what is running (live experiments and campaigns). Start every session here: it is designed to answer "what should I do next?" in under a minute.',
	},
	{
		ico: '✣',
		name: 'Clustering Studio',
		view: 'clustering',
		text: 'Behavioral segment discovery with explainability. The engine clusters visitors by behavior (cart value, funnel depth, coupon use, recency) and explains each segment in plain language: who they are, how large, what they respond to, and how confident the model is. Segments are not activated automatically — each carries an activation-readiness state and a recommended tactic you can send to the Builder.',
	},
	{
		ico: '▣',
		name: 'Campaign Builder',
		view: 'builder',
		text: 'Turns an approved opportunity or a discovered segment into a campaign draft: audience, offer logic, trigger rules (exit intent, browsing delay, intent score), creative preview, and personalization tokens. "Activate" never launches anything directly — it runs the launch-readiness checklist and submits the draft to the Approval Queue.',
	},
	{
		ico: '⚗',
		name: 'Experiment Lab',
		view: 'experiment-lab',
		text: 'Where causal truth lives. Create and manage experiments — split tests, lift tests, holdouts, and adaptive (Thompson Sampling) tests that shift traffic toward winning variants while still learning. Shows allocation history, Bayesian confidence ranges, and guardrail metrics. Declaring a winner — even via "Instant Winner" — is a governed action that routes through the Approval Queue.',
	},
	{
		ico: '◫',
		name: 'Intelligence Reporting',
		view: 'reporting',
		text: 'The executive layer. Influenced revenue, conversion lift, campaign effectiveness with ROI, the conversion funnel, and — most importantly — ranked next-best opportunities and AI-generated theories to test. Built for QBRs: every number is labeled as projected, influenced, or holdout-measured so it can be defended in front of a VP.',
	},
	{
		ico: '◬',
		name: 'Deep Research',
		view: 'deep-research',
		text: 'The research layer for multi-factor questions. It pulls together campaign performance, tests, segments, caveats, and governance policy into a sourced report with a direct answer, evidence trail, next actions, and risks.',
	},
	{
		ico: '✓',
		name: 'Approval Queue',
		view: 'approvals',
		text: 'Human-in-the-loop control. Every AI-proposed action arrives here with its evidence, projected impact (including margin cost, shown honestly), risk level, required approvers, SLA, and an attached rollback plan. Approve, reject, or request changes — every decision is recorded immutably.',
	},
	{
		ico: '☰',
		name: 'Audit Log',
		view: 'audit-log',
		text: 'The immutable record of decisions and outcomes. Internal View includes every AI recommendation, system action, launch, pause, winner declaration, rollback, before/after diff, and decision reason.',
	},
];

const GLOSSARY = [
	['Targeted Tactic (TT / Precise Promotion)', 'On-site campaign engaging a specific audience with an offer or message — Upsellit’s core on-site solution family.'],
	['Lead Capture (LC / Inboxed Incentive)', 'Identity-capture campaign: collects email/SMS in exchange for an incentive, cart save, or back-in-stock alert.'],
	['Email Remarketing (EM / Hound)', 'Pre/post-capture email flows re-engaging abandoners after they leave the site.'],
	['Anonymous Visitor ID', 'Identifies opted-in visitors without cookies to extend remarketing reach to otherwise anonymous traffic.'],
	['Coupon Corrector', 'Catches failed coupon attempts at checkout and offers a valid, pre-approved alternative.'],
	['Ad Extension Blocker', 'Prevents third-party browser extensions from injecting competitor ads and coupons on your site.'],
	['Influenced revenue', 'Revenue from sessions a campaign touched. Useful direction, but not proof — see incremental revenue.'],
	['Incremental revenue', 'Revenue above the control/holdout group. The only number treated as proof on this platform.'],
	['RPV / RPS', 'Revenue per visitor / per session — the primary economic comparison metric between variants.'],
	['Confidence', 'Statistical significance (or Bayesian posterior probability), never a vague feeling. Model confidence and measurement confidence are reported separately.'],
	['Opportunity score', 'Weighted ranking of revenue headroom, intent depth, reach, measurability, speed to value, and risk.'],
	['Readiness check', 'The pre-launch gate: event coverage, audience stability, creative, offer governance, QA, measurement plan, approval path.'],
	['Suppression rules', 'Who a campaign must NOT touch: recent purchasers, consent-denied visitors, conflicting-campaign audiences.'],
	['data_as_of', 'The freshness timestamp on every metric. Honest staleness beats fake realtime.'],
];

export default function Help() {
	const { navigate, isInternal } = useCop();
	const [active, setActive] = useState('overview');
	const refs = useRef({});
	const visibleSections = isInternal ? SECTIONS : SECTIONS.filter((s) => s.id !== 'autopilot');

	const jump = (id) => {
		setActive(id);
		refs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((e) => {
					if (e.isIntersecting) setActive(e.target.dataset.section);
				});
			},
			{ rootMargin: '-90px 0px -70% 0px' }
		);
		Object.values(refs.current).forEach((el) => el && observer.observe(el));
		return () => observer.disconnect();
	}, []);

	const section = (id, node) => (
		<div ref={(el) => (refs.current[id] = el)} data-section={id} style={{ scrollMarginTop: 80 }}>
			{node}
		</div>
	);

	return (
		<main className="cop-page">
			<div className="cop-banner cop-contact-banner">
				<img className="cop-contact-avatar" src={serviceImg} alt="Your Upsellit conversion specialist" />
				<div>
					<h3>Need a hand? Talk to your Upsellit conversion team.</h3>
					<p>
						A dedicated specialist is ready to help with strategy, setup, or any campaign in flight —
						no bots, a real person who knows your account.
					</p>
				</div>
				<a className="cop-btn primary" href="https://us.upsellit.com/contact/" target="_blank" rel="noreferrer">
					Get in touch →
				</a>
			</div>

			<div className="cop-help-hero">
				<UpsellyMascot size={180} />
				<div>
					<h2>Campaign Optimization Platform</h2>
					<p>
						AI-assisted revenue optimization for ecommerce teams, with human-approved actions by default.
						The platform reads your behavioral data, finds the highest-value opportunities, drafts the
						campaigns and experiments to capture them, routes every action through governance, and proves
						the result with holdout-measured incremental revenue — not vanity attribution.
					</p>
				</div>
			</div>

			<div className="cop-help-layout">
				<nav className="cop-help-toc">
					{visibleSections.map((s) => (
						<button key={s.id} className={active === s.id ? 'active' : ''} onClick={() => jump(s.id)}>
							{s.label}
						</button>
					))}
				</nav>

				<div className="cop-help-body">
					{section(
						'overview',
						<Card title="What this platform is" icon="✦">
							<p>
								This is a <b>commerce revenue decision layer</b>, not a generic CDP, ESP, or testing tool.
								It sits on top of the data Upsellit already collects — sessions, ecommerce events, carts,
								campaign exposures, catalog and identity signals — and turns it into an ordered queue of
								commercially meaningful, approvable, measurable actions.
							</p>
							<p>It makes four promises, and every screen serves one of them:</p>
							<ul>
								<li><b>Find the highest-value opportunities</b> — funnel diagnosis and opportunity scoring, not raw dashboards.</li>
								<li><b>Generate revenue actions</b> — campaign drafts and experiment designs, not blank canvases.</li>
								<li><b>Enforce approvals and guardrails</b> — brand, margin, and compliance protection on every automated action.</li>
								<li><b>Prove incremental lift</b> — holdouts and experiments separate what worked from what merely happened.</li>
							</ul>
							<p>
								The operator test for every screen: <i>does this tell an ecommerce manager what to do next,
								and can they defend the result to their VP?</i>
							</p>
						</Card>
					)}

					{section(
						'lifecycle',
						<Card title="The action lifecycle" icon="↻">
							<p>
								Everything is organized around one closed loop — not around separate tools. Each stage hands
								typed, auditable objects to the next:
							</p>
							<div className="cop-flow">
								{LIFECYCLE.map((step, i) => (
									<React.Fragment key={step}>
										<span className={`cop-flow-step${step === 'Approve' ? ' hot' : ''}`}>{step}</span>
										{i < LIFECYCLE.length - 1 && <span className="cop-flow-arrow">→</span>}
									</React.Fragment>
								))}
							</div>
							<ul>
								<li><b>Detect → Score:</b> behavioral data is diagnosed by funnel stage (bounce, product, cart, checkout abandonment, post-purchase, lapsed) and scored for revenue headroom, intent depth, reach, and measurability.</li>
								<li><b>Recommend → Validate:</b> the AI proposes a specific action with evidence and projected impact; the launch-readiness checklist blocks anything missing data, QA, measurement, or governance.</li>
								<li><b>Approve → Launch:</b> an Upsellit reviewer approves (editing the proposal if needed), then the client approves where the policy requires it — only then does anything go live. Low-risk allowlisted actions auto-approve by policy.</li>
								<li><b>Measure → Learn:</b> lift tests and holdouts measure true incremental impact, and the result feeds back into scoring.</li>
							</ul>
						</Card>
					)}

					{section(
						'modules',
						<Card title="The eight modules" icon="▦">
							<div className="cop-help-grid">
								{MODULES.map((m) => (
									<div className="cop-help-tile" key={m.name}>
										<h5>
											<span className="ico">{m.ico}</span>
											{m.name}
										</h5>
										<p>{m.text}</p>
										<button className="cop-link" onClick={() => navigate(m.view)}>
											Open {m.name} →
										</button>
									</div>
								))}
							</div>
						</Card>
					)}

					{section(
						'concepts',
						<Card title="Core concepts — six objects, kept separate" icon="◇">
							<p>
								The platform never blurs these together. Keeping them separate is what makes the system
								governable, measurable, and safe to automate:
							</p>
							<div className="cop-term-grid">
								<div className="cop-term"><b>Opportunity</b><span>A problem worth solving — e.g. "checkout abandoners with $300+ carts" — scored and ranked.</span></div>
								<div className="cop-term"><b>Recommendation</b><span>The AI’s proposed action: inspectable, explainable, approvable, rejectable, reversible.</span></div>
								<div className="cop-term"><b>Campaign</b><span>The executable intervention: audience, triggers, creative, offer, suppression, frequency caps.</span></div>
								<div className="cop-term"><b>Experiment</b><span>The measurement design: split test, lift test, bandit, or holdout, with a decision rule.</span></div>
								<div className="cop-term"><b>Approval</b><span>Permission to act — risk-tiered, SLA-tracked, recorded against a versioned policy.</span></div>
								<div className="cop-term"><b>Audit record</b><span>What actually happened, immutably, with before/after state for rollback.</span></div>
							</div>
						</Card>
					)}

					{section(
						'measurement',
						<Card title="How we measure — projected vs influenced vs incremental" icon="∑">
							<p>Three different numbers appear across the platform, and they are never interchangeable:</p>
							<ul>
								<li><b>Projected uplift</b> — a model estimate of future opportunity. Shown on opportunity cards and Builder projections, always labeled as a projection. Used for prioritization, never for proof.</li>
								<li><b>Influenced revenue</b> — revenue touched by a campaign. Directionally useful, but inflatable; treat as a ceiling, not a result.</li>
								<li><b>Incremental revenue</b> — revenue above a randomized control or holdout. This is the number that counts, the number in lift-test reports, and the number to take to a QBR.</li>
							</ul>
							<p>
								Operating norms: the expected lift band is <b>5–15%</b>. Above 15% triggers strategic review
								(possible over-aggressive optimization or cannibalization); below 5% marks an under-optimized
								account and an expansion opportunity. Lift tests run 30 days or to statistical significance,
								whichever comes first. Every metric carries a <span className="cop-mono">data_as_of</span> freshness
								label — honest staleness beats fake realtime.
							</p>
						</Card>
					)}

					{section(
						'experiments',
						<Card title="Experiment types — never used interchangeably" icon="⚗">
							<div className="cop-term-grid">
								<div className="cop-term"><b>Split test</b><span>Upsellit variation vs Upsellit variation. Answers "which version is better?" Use for new strategy validation.</span></div>
								<div className="cop-term"><b>Lift test</b><span>Upsellit vs no Upsellit (suppressed control). Answers "are we adding value at all?" The incrementality backbone.</span></div>
								<div className="cop-term"><b>Bandit test</b><span>Adaptive allocation (Thompson Sampling) shifting traffic toward winners while learning. Use to scale a validated strategy.</span></div>
								<div className="cop-term"><b>Holdout</b><span>A no-treatment group preserved for truth. Portfolio-level holdouts keep influenced-revenue claims honest.</span></div>
							</div>
							<p>
								Operating rule: <b>A/B first for strategy, bandit second for scaling, holdout always for truth.</b>{' '}
								Statistical significance is labeled "Confidence" throughout, matching Upsellit’s client
								reporting conventions.
							</p>
						</Card>
					)}

					{section(
						'governance',
						<Card title="Governance & approvals" icon="✓">
							{isInternal ? (
								<>
									<p>
										Approval is a staged pipeline: <b>the AI proposes → an Upsellit reviewer approves (and may
										edit the proposal — copy, offer, targeting — before the client ever sees it) → the client
										approves → the action launches.</b> Every edit is audited with before/after state. An
										action’s risk level determines which stages its account policy requires:
									</p>
									<ul>
										<li><Pill tone="green">Low</Pill> &nbsp;Drafts, reports, suppression hygiene — auto-approved when on the pre-authorized allowlist, still fully audited.</li>
										<li><Pill tone="blue">Medium</Pill> &nbsp;Campaign creation, timing changes, winner declarations — internal Upsellit review.</li>
										<li><Pill tone="amber">High</Pill> &nbsp;Discount changes, checkout messaging, suppression logic — internal review, then client approval.</li>
										<li><Pill tone="red">Critical</Pill> &nbsp;Pricing-sensitive, legal-claim, or sitewide actions — internal review, then client approval, with an exportable review record.</li>
									</ul>
								</>
							) : (
								<>
									<p>
										Actions that need your signoff appear in the Approval Queue with the reviewed proposal,
										evidence, projected impact, and rollback plan. Nothing launches until the required approval is recorded.
									</p>
									<ul>
										<li><Pill tone="amber">High</Pill> &nbsp;Discount changes, checkout messaging, and sensitive audience changes require client approval.</li>
										<li><Pill tone="red">Critical</Pill> &nbsp;Pricing-sensitive, legal-claim, or sitewide actions require an exportable approval record.</li>
									</ul>
								</>
							)}
							<p>
								<b>Clients approve inside Client Reporting</b> — their existing login-protected dashboard gains
								an Approvals section showing the reviewed proposal with evidence and projected impact. Each
								client chooses how to hear about it: an <b>immediate email</b> per action entering their review,
								or a <b>daily summary</b> of everything awaiting decision and recent outcomes (the default).
							</p>
							{isInternal && (
								<p>
									<b>SLA windows are internal routing timers, not a support promise.</b> Clocks run in business
									hours across US Pacific, US Eastern, and UK regions, and pause outside coverage. An expired SLA
									escalates or expires the request — it <b>never approves anything by default</b>, and no campaign
									launches while its approval waits. Every request ships with evidence, projected impact (margin
									cost included, shown in red when negative), and a rollback plan.
								</p>
							)}
						</Card>
					)}

					{isInternal && section(
						'autopilot',
						<InternalCard title="What the Auto-Pilot toggle actually does" icon="◉">
							<p>
								Auto-Pilot is a <b>per-account allowlist of pre-authorized, low-risk action types</b> — report
								generation, segment monitoring, draft creation, underperformer alerts, and protective
								suppression updates. It is not "the AI launches things."
							</p>
							<ul>
								<li>It never bypasses the approval pathway — pre-authorized actions are still created as actions, auto-approved by policy, and written to the audit log.</li>
								<li>Anything touching offers, money, customer experience, or audiences still requires a human.</li>
								<li>Time-critical safety (auto-pausing an underperformer, guardrail breaches) works through these pre-authorized policies precisely so safety never depends on someone being awake.</li>
							</ul>
							<div className="cop-aop-links">
								<AopLink view="pending-actions">AOP pending actions</AopLink>
								<AopLink view="workflow-runs">AOP workflow runs</AopLink>
							</div>
						</InternalCard>
					)}

					{section(
						'data',
						<Card title="Data sources & freshness" icon="⇄">
							<p>
								The platform reads through <b>authenticated external APIs</b> — never directly from external
								databases — and stores only decisions, governance records, and derived aggregates. Raw session
								and event data stays in the systems that collect it:
							</p>
							<ul>
								<li><b>Upsellit Admin & Client Reporting</b> — campaign configuration, pixel health, per-solution performance, lift-test results.</li>
								<li><b>Shopify analytics</b> — hourly abandonment, conversion, behavioral, and coupon aggregates, plus the purchase-probability model.</li>
								<li><b>Salesforce</b> — account ownership, commercial context, approval routing.</li>
								<li><b>CSD</b> — campaign requirements and creative specifications for managed builds.</li>
							</ul>
							<p>
								If a connection degrades, the platform says so: readiness drops, dependent recommendations gain
								a data blocker and cannot reach approval, and dashboards show the stale{' '}
								<span className="cop-mono">data_as_of</span>. It never silently computes on partial data.
							</p>
						</Card>
					)}

					{section(
						'glossary',
						<Card title="Glossary" icon="❡">
							<div className="cop-term-grid">
								{GLOSSARY.map(([term, def]) => (
									<div className="cop-term" key={term}>
										<b>{term}</b>
										<span>{def}</span>
									</div>
								))}
							</div>
							<p style={{ marginTop: 14 }}>
								Questions this page doesn’t answer?{' '}
								{isInternal ? (
									<>Ask in <b>#campaign-optimization</b> or email <b>convert@upsellit.com</b>.</>
								) : (
									<>Email <b>convert@upsellit.com</b>.</>
								)}
							</p>
						</Card>
					)}

					{isInternal && (
						<InternalCard title="Operational References" icon="⇄" right="internal only">
							<p style={{ marginTop: 0, color: 'var(--text-sec)', fontSize: 12.5, lineHeight: 1.65 }}>
								Use these AOP pages to trace generated packets, approval handoffs, and workflow outcomes behind this COP view.
							</p>
							<div className="cop-aop-links">
								<AopLink view="overview">AOP overview</AopLink>
								<AopLink view="reports">AOP reports</AopLink>
								<AopLink view="audit-log">AOP audit</AopLink>
							</div>
						</InternalCard>
					)}
				</div>
			</div>
		</main>
	);
}
